import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import PublicSitePage from './PublicSitePage';
import { getCorePageFallback } from '../../lib/corePageLayouts';
import { supabase } from '../../lib/supabase';

type RouteStatus = 'loading' | 'ready' | 'missing';

function hasPublishedSections(value: unknown) {
  if (!value || typeof value !== 'object') return false;
  const sections = (value as { sections?: unknown }).sections;
  return Array.isArray(sections) && sections.length > 0;
}

const PublicPreviewRoute: React.FC = () => {
  const { pageKey: pageKeyParam } = useParams<{ pageKey: string }>();
  const pageKey = (pageKeyParam ?? '').trim().toLowerCase();
  const fallbackLayout = getCorePageFallback(pageKey);
  const [status, setStatus] = useState<RouteStatus>('loading');

  useEffect(() => {
    let cancelled = false;

    const loadPage = async () => {
      if (!pageKey || pageKey === 'intro') {
        if (!cancelled) setStatus('missing');
        return;
      }

      try {
        const { data, error } = await supabase
          .from('page_layouts_public')
          .select('page_key, published_content')
          .eq('page_key', pageKey)
          .maybeSingle<{ page_key: string; published_content: unknown | null }>();

        if (cancelled) return;
        if (error) throw error;

        const hasPublishedContent = hasPublishedSections(data?.published_content);
        setStatus(hasPublishedContent || fallbackLayout ? 'ready' : 'missing');
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn(`[PublicPreviewRoute] Failed to resolve page ${pageKey}`, error);
        }
        if (!cancelled) setStatus(fallbackLayout ? 'ready' : 'missing');
      }
    };

    setStatus('loading');
    void loadPage();

    return () => {
      cancelled = true;
    };
  }, [fallbackLayout, pageKey]);

  if (!pageKey) {
    return <Navigate to="/preview/home" replace />;
  }

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stone-950 text-white">
        <div className="text-center">
          <div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-b-2 border-white" />
          <p className="text-lg">Loading preview...</p>
        </div>
      </div>
    );
  }

  if (status === 'missing') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stone-50 px-6 py-16">
        <div className="max-w-lg rounded-3xl border border-stone-200 bg-white p-10 text-center shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">
            Preview page not found
          </p>
          <h1 className="mt-3 text-3xl font-semibold text-stone-950">
            This preview page is not available.
          </h1>
          <p className="mt-4 text-sm leading-6 text-stone-600">
            Return to the preview home page or open the live holding page.
          </p>
          <Link
            to="/preview/home"
            className="mt-6 inline-flex rounded-full bg-stone-950 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-stone-800"
          >
            Go to preview home
          </Link>
        </div>
      </div>
    );
  }

  return <PublicSitePage pageKey={pageKey} fallbackLayout={fallbackLayout ?? undefined} />;
};

export default PublicPreviewRoute;
